const RESIZABLE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

/**
 * Resize a file to a max dimension for use as a preview placeholder.
 * Returns a blob URL of the resized image, or falls back to a blob URL
 * of the original file if the image doesn't need resizing or on any error.
 */
export async function resizeImageForPreview(
  file: File,
  maxSize = 800,
  quality = 0.8
): Promise<string> {
  if (!RESIZABLE_TYPES.has(file.type)) {
    return window.URL.createObjectURL(file);
  }

  try {
    const bitmap = await createImageBitmap(file);
    try {
      const scale = Math.min(
        1,
        maxSize / Math.max(bitmap.width, bitmap.height)
      );

      if (scale >= 1) {
        return window.URL.createObjectURL(file);
      }

      const width = Math.round(bitmap.width * scale);
      const height = Math.round(bitmap.height * scale);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return window.URL.createObjectURL(file);
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(bitmap, 0, 0, width, height);

      const outputType = file.type === "image/png" ? "image/png" : "image/jpeg";
      const blob = await new Promise<Blob | null>(resolve => {
        canvas.toBlob(
          resolve,
          outputType,
          outputType === "image/jpeg" ? quality : undefined
        );
      });

      if (!blob) {
        return window.URL.createObjectURL(file);
      }

      return window.URL.createObjectURL(blob);
    } finally {
      bitmap.close();
    }
  } catch {
    return window.URL.createObjectURL(file);
  }
}
